/**
 * Messaging Connector — routes messenger events through Supabase + QStash.
 * Server-side only; registered in mycelium-init for browser backbone parity.
 *
 * @module mycelium-messaging
 * @since 2026-05-26
 */

import {
  myceliumEventBackbone,
  type MyceliumEvent,
  type EventDomain,
} from "./mycelium-event-backbone.service";
import { getMyceliumTransport } from "./mycelium-transport-registry";
import type {
  ConnectorMetadata,
  IMyceliumConnector,
} from "./mycelium-connector.interface";
import { logger } from "../lib/logger";

export class MessagingConnector implements IMyceliumConnector {
  readonly metadata: ConnectorMetadata = {
    id: "messaging-connector",
    name: "Messaging Connector",
    description: "Routes inbound messenger events to worker/QStash dispatch",
    phase: 8,
    subscribedDomains: ["messaging"],
    publishedDomains: ["messaging", "notification"],
    requiresPgmq: true,
    serverOnly: true,
  };

  initialized = false;

  async initialize(): Promise<void> {
    if (this.initialized) return;

    myceliumEventBackbone.subscribe({
      name: "MessagingConnector-Inbound",
      domains: ["messaging"],
      eventTypes: ["messaging.message.received"],
      handler: this.handleInbound.bind(this),
      priority: 8,
    });

    myceliumEventBackbone.subscribe({
      name: "MessagingConnector-Reply",
      domains: ["messaging"],
      eventTypes: ["messaging.reply.ready"],
      handler: this.handleReplyReady.bind(this),
      priority: 7,
    });

    this.initialized = true;
    logger.info("MessagingConnector initialized");
  }

  async dispose(): Promise<void> {
    this.initialized = false;
  }

  private async handleInbound(event: MyceliumEvent): Promise<void> {
    logger.debug("Messaging inbound event (browser relay)", {
      type: event.type,
      inboxId: (event.payload as Record<string, unknown>).inboxId,
    });
    // Worker is authoritative; browser subscriber is audit-only.
  }

  private async handleReplyReady(event: MyceliumEvent): Promise<void> {
    await getMyceliumTransport().publish({
      domain: "messaging",
      type: "messaging.reply.ready",
      userId: event.userId,
      payload: event.payload as Record<string, unknown>,
    });
  }
}

export const messagingConnector = new MessagingConnector();
